import { Flex, Icon, Menu, Portal, Text } from '@chakra-ui/react';
import { BiChevronDown } from 'react-icons/bi';
import { useUserQuery } from '@/api/user/useUserQuery';
import { setOrganizationId } from '@/context/authClient';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { SindiButton } from '@/components/button/SindiButton';

interface OrgSwitcherProps {
  onSwitch?: () => void;
}

function OrgSwitcher({ onSwitch }: OrgSwitcherProps) {
  const { data: user } = useUserQuery();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const hasMultipleOrgs = (user?.organizations?.length ?? 0) > 1;

  if (!user?.organization) return null;

  const handleSelectOrganization = (orgId: string) => {
    setOrganizationId(orgId);
    queryClient.invalidateQueries();
    navigate('/');
    onSwitch?.();
  };

  const trigger = (
    <SindiButton
      variant="ghost"
      w="full"
      justifyContent="flex-start"
      color="white"
      mb={2}
      _hover={{ bg: 'gray.700' }}
      _active={{ bg: 'gray.700' }}
      _expanded={{ bg: 'gray.700' }}
      cursor={hasMultipleOrgs ? 'pointer' : 'default'}
    >
      <Flex
        align="center"
        justify="center"
        bg="sindiPurple.500"
        borderRadius="lg"
        w={6}
        h={6}
        flexShrink={0}
      >
        <Text color="gray.800" fontWeight="bold" fontSize="xs">
          {user.organization.name[0].toUpperCase()}
        </Text>
      </Flex>
      <Text fontWeight="medium" flex={1} lineClamp={1} textAlign="start">
        {user.organization.name}
      </Text>
      {hasMultipleOrgs && (
        <Icon as={BiChevronDown} color="gray.400" boxSize={4} />
      )}
    </SindiButton>
  );

  if (!hasMultipleOrgs) return trigger;

  return (
    <Menu.Root>
      <Menu.Trigger asChild>{trigger}</Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content bg="gray.700" borderColor="gray.600">
            {user.organizations?.map((org) => (
              <Menu.Item
                key={org.id}
                value={org.id}
                color="white"
                fontWeight={
                  org.id === user.organization?.id ? 'semibold' : 'normal'
                }
                _hover={{ bg: 'gray.600' }}
                onClick={() => handleSelectOrganization(org.id)}
              >
                {org.name}
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

export default OrgSwitcher;
